import { gql } from '@apollo/client';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useMatch, useNavigate } from '@tanstack/react-location';
import { memo, useEffect, useState } from 'react';
import PageContainer from '../../core/components/PageContainer';
import { usePropertyId } from '../../core/hooks/usePropertyId';
import copyText from '../../core/util/copyText';
import { baseUrl } from '../../main/client';
import importCodeTemplate from '../../booking/automation/importCodeTemplate';
import PropertySelectionDialog from '../../property/components/PropertySelectionDialog';

interface GuestCheckinData {
    firstName: string;
    lastName: string;
    sex: string;
    nationality: string;
    country: string;
    birthDate: string;
    birthPlace: string;
    postcode: string;
    idDocumentType?: string;
    idDocumentId?: string;
    nationalityNameHU: string;
    countryNameHU: string;
}

interface QueueAddResponse {
    success: boolean;
    error?: string;
    command?: {
        id: string;
        name: string;
    };
}

const getExtensionId = (): string | null => {
    return typeof window !== 'undefined'
        ? (window as Window & { __MUSHROOM_EXTENSION_ID__?: string }).__MUSHROOM_EXTENSION_ID__ ?? null
        : null;
};

const sendGuestCheckinQueueCommand = (guest: GuestCheckinData, bookingId: number): Promise<QueueAddResponse> => {
    return new Promise((resolve) => {
        const extensionId = getExtensionId();
        if (!extensionId) {
            resolve({ success: false, error: 'Extension not detected' });
            return;
        }
        const chromeRuntime = (globalThis as { chrome?: { runtime?: { sendMessage: (
            extensionId: string,
            message: unknown,
            callback: (response: QueueAddResponse) => void
        ) => void } } }).chrome;
        if (!chromeRuntime?.runtime?.sendMessage) {
            resolve({ success: false, error: 'Chrome runtime not available' });
            return;
        }
        const message = {
            type: 'QUEUE_ADD',
            commandType: 'GUEST_CHECKIN_IMPORT',
            name: `${guest.firstName} ${guest.lastName}`,
            targetDomain: 'vendegem.hu',
            targetUrl: 'https://vendegem.hu/',
            data: guest,
            sourceBookingId: bookingId,
        };
        chromeRuntime.runtime.sendMessage(
            extensionId,
            message,
            (response) => {
                if (response) {
                    resolve(response);
                } else {
                    resolve({ success: false, error: 'No response from extension' });
                }
            }
        );
    });
};

interface Guest {
    id: number;
    firstName: string;
    lastName: string;
    sex: string;
    nationality: string;
    country: string;
    birthDate: string;
    birthPlace: string;
    postcode: string;
    address: string;
    idDocumentType?: string;
    idDocumentId?: string;
    idDocumentIsPdf: boolean;
    valid: boolean;
    price: number;
    touristTax: number;
    nationalityNameHU: string;
    countryNameHU: string;
}

const checkinQuery = gql`
    query booking($id: Int!) {
        booking(id: $id) {
            id
            guestName
            checkin
            checkout
            guests {
                id
                firstName
                lastName
                sex
                nationality
                nationalityNameHU
                country
                countryNameHU
                birthDate
                birthPlace
                postcode
                address
                idDocumentType
                idDocumentId
                idDocumentIsPdf
                valid
                price
                touristTax
            }
        }
    }
`;

const markCheckinCompleteMutation = gql`
    mutation alterBookingState($id: Int!, $fields: BookingStateInput) {
        alterBookingState(id: $id, fields: $fields) {
            id
            hasInstructions
        }
    }
`;

const GuestCheckinCard = ({
    guest,
    bookingId,
    guestIndex,
    onMarkDone,
    isDone,
}: {
    guest: Guest;
    bookingId: number;
    guestIndex: number;
    onMarkDone: () => void;
    isDone: boolean;
}) => {
    const [copiedAutomationCode, setCopiedAutomationCode] = useState(false);
    const [copiedPostcode, setCopiedPostcode] = useState(false);
    const [copiedBirthPlace, setCopiedBirthPlace] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [rotation, setRotation] = useState('0deg');
    const [extensionInstalled, setExtensionInstalled] = useState(false);
    const [queueStatus, setQueueStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [queueError, setQueueError] = useState<string | null>(null);

    useEffect(() => {
        // Check for extension after a short delay to allow content script to inject
        const timer = setTimeout(() => {
            setExtensionInstalled(getExtensionId() !== null);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    const handleQueueToExtension = async () => {
        setQueueStatus('loading');
        setQueueError(null);

        const guestData: GuestCheckinData = {
            firstName: guest.firstName,
            lastName: guest.lastName,
            sex: guest.sex,
            nationality: guest.nationality,
            country: guest.country,
            birthDate: guest.birthDate,
            birthPlace: guest.birthPlace,
            postcode: guest.postcode,
            idDocumentType: guest.idDocumentType,
            idDocumentId: guest.idDocumentId,
            nationalityNameHU: guest.nationalityNameHU,
            countryNameHU: guest.countryNameHU,
        };

        const response = await sendGuestCheckinQueueCommand(guestData, bookingId);

        if (response.success) {
            setQueueStatus('success');
        } else {
            setQueueStatus('error');
            setQueueError(response.error ?? 'Failed to queue');
        }
    };

    const importCode = `
        ${importCodeTemplate}(${JSON.stringify(guest)}).catch(error => {
            throw error;
        });
    `;

    const documentUrl = `${baseUrl}/api/guest/document/${bookingId}/${guestIndex}?key=${localStorage.getItem('token')}`;
    const isPdf = guest.idDocumentIsPdf;

    if (isDone) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    border: '2px solid',
                    borderColor: 'success.main',
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                }}
            >
                <Typography sx={{ fontWeight: 700 }}>
                    {guest.firstName} {guest.lastName}
                </Typography>
                <Checkbox checked disabled sx={{ color: 'success.main' }} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                border: '3px solid #6366f1',
                backgroundColor: '#1a1a2e',
                p: 0,
            }}
        >
            <Box
                sx={{
                    borderBottom: '2px solid #6366f1',
                    backgroundColor: '#0f0f23',
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 900,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                    }}
                >
                    {guest.firstName} {guest.lastName}
                </Typography>
                <Box
                    sx={{
                        px: 2,
                        py: 0.5,
                        border: '2px solid #374151',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                    }}
                >
                    {guest.country}
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 3,
                    p: 3,
                }}
            >
                {isPdf ? (
                    <Box
                        onClick={() => setDialogOpen(true)}
                        sx={{
                            height: '400px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#1a1a2e',
                            cursor: 'pointer',
                            border: '2px solid #374151',
                            '&:hover': {
                                borderColor: '#6366f1',
                            },
                        }}
                    >
                        <Typography
                            sx={{
                                color: '#9ca3af',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                            }}
                        >
                            PDF Document - Click to view
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        onClick={() => setDialogOpen(true)}
                        sx={{
                            height: '400px',
                            backgroundImage: `url(${documentUrl})`,
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                            cursor: 'pointer',
                            border: '2px solid #374151',
                            '&:hover': {
                                borderColor: '#6366f1',
                            },
                        }}
                    />
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        onClick={() => {
                            copyText(importCode)
                                .then(() => {
                                    setCopiedAutomationCode(true);
                                })
                                .catch(error => {
                                    throw error;
                                });
                        }}
                        sx={{
                            borderRadius: 0,
                            border: '2px solid',
                            borderColor: 'secondary.main',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            py: 1.5,
                        }}
                    >
                        {copiedAutomationCode ? 'Copied ✓' : 'Copy Automation Code'}
                    </Button>

                    {extensionInstalled && (
                        <Button
                            variant="outlined"
                            color="primary"
                            fullWidth
                            onClick={handleQueueToExtension}
                            disabled={queueStatus === 'loading' || queueStatus === 'success'}
                            sx={{
                                borderRadius: 0,
                                border: '2px solid',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                py: 1.5,
                                '&:hover': {
                                    borderWidth: '2px',
                                },
                            }}
                        >
                            {queueStatus === 'loading' ? 'Queueing...' :
                             queueStatus === 'success' ? 'Queued ✓' :
                             'Queue to Extension'}
                        </Button>
                    )}

                    {queueStatus === 'success' && (
                        <Alert
                            severity="success"
                            sx={{
                                borderRadius: 0,
                                border: '2px solid #22c55e',
                                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                                fontWeight: 600,
                                '& .MuiAlert-icon': {
                                    color: '#22c55e',
                                },
                            }}
                        >
                            Queued! Open extension popup to execute on vendegem.hu
                        </Alert>
                    )}

                    {queueStatus === 'error' && queueError && (
                        <Alert
                            severity="error"
                            onClose={() => setQueueError(null)}
                            sx={{
                                borderRadius: 0,
                                border: '2px solid #ef4444',
                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                fontWeight: 600,
                                '& .MuiAlert-icon': {
                                    color: '#ef4444',
                                },
                            }}
                        >
                            {queueError}
                        </Alert>
                    )}

                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => {
                            copyText(guest.birthPlace)
                                .then(() => {
                                    setCopiedBirthPlace(true);
                                })
                                .catch(error => {
                                    throw error;
                                });
                        }}
                        sx={{
                            borderRadius: 0,
                            borderWidth: '2px',
                            borderColor: '#374151',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            py: 1,
                            '&:hover': {
                                borderWidth: '2px',
                                borderColor: '#6366f1',
                            },
                        }}
                    >
                        Birth Place: {guest.birthPlace} {copiedBirthPlace ? '✓' : ''}
                    </Button>

                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => {
                            copyText(guest.postcode)
                                .then(() => {
                                    setCopiedPostcode(true);
                                })
                                .catch(error => {
                                    throw error;
                                });
                        }}
                        sx={{
                            borderRadius: 0,
                            borderWidth: '2px',
                            borderColor: '#374151',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            py: 1,
                            '&:hover': {
                                borderWidth: '2px',
                                borderColor: '#6366f1',
                            },
                        }}
                    >
                        Postcode: {guest.postcode} {copiedPostcode ? '✓' : ''}
                    </Button>

                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => window.open(documentUrl)}
                        sx={{
                            borderRadius: 0,
                            borderWidth: '2px',
                            borderColor: '#374151',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            py: 1,
                            '&:hover': {
                                borderWidth: '2px',
                                borderColor: '#6366f1',
                            },
                        }}
                    >
                        Open Document
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={onMarkDone}
                        sx={{
                            borderRadius: 0,
                            border: '2px solid #6366f1',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            py: 1.5,
                            mt: 'auto',
                        }}
                    >
                        Mark as Done
                    </Button>
                </Box>
            </Box>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullScreen>
                <DialogContent sx={{ backgroundColor: '#0f0f23', p: 0 }}>
                    {isPdf ? (
                        <embed
                            src={documentUrl}
                            type="application/pdf"
                            style={{
                                width: '100%',
                                height: '90vh',
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                height: '90vh',
                                width: '100%',
                                transform: `rotate(${rotation})`,
                                backgroundImage: `url(${documentUrl})`,
                                backgroundSize: 'contain',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center',
                            }}
                        />
                    )}
                </DialogContent>
                <DialogActions
                    sx={{
                        backgroundColor: '#1a1a2e',
                        borderTop: '3px solid #6366f1',
                        p: 2,
                        gap: 1,
                    }}
                >
                    {!isPdf && (
                        <>
                            <Button
                                onClick={() => setRotation('90deg')}
                                variant="outlined"
                                sx={{
                                    borderRadius: 0,
                                    borderWidth: '2px',
                                    borderColor: '#374151',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    '&:hover': {
                                        borderWidth: '2px',
                                        borderColor: '#6366f1',
                                    },
                                }}
                            >
                                Rotate 90
                            </Button>
                            <Button
                                onClick={() => setRotation('270deg')}
                                variant="outlined"
                                sx={{
                                    borderRadius: 0,
                                    borderWidth: '2px',
                                    borderColor: '#374151',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    '&:hover': {
                                        borderWidth: '2px',
                                        borderColor: '#6366f1',
                                    },
                                }}
                            >
                                Rotate 270
                            </Button>
                            <Button
                                onClick={() => setRotation('0deg')}
                                variant="outlined"
                                sx={{
                                    borderRadius: 0,
                                    borderWidth: '2px',
                                    borderColor: '#374151',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    '&:hover': {
                                        borderWidth: '2px',
                                        borderColor: '#6366f1',
                                    },
                                }}
                            >
                                Reset
                            </Button>
                        </>
                    )}
                    <Button
                        onClick={() => window.open(documentUrl)}
                        variant="outlined"
                        sx={{
                            borderRadius: 0,
                            borderWidth: '2px',
                            borderColor: '#374151',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            '&:hover': {
                                borderWidth: '2px',
                                borderColor: '#6366f1',
                            },
                        }}
                    >
                        Open in New Tab
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Button
                        onClick={() => setDialogOpen(false)}
                        variant="contained"
                        color="primary"
                        sx={{
                            borderRadius: 0,
                            border: '2px solid #6366f1',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const CheckinPage = memo(() => {
    const propertyId = usePropertyId();
    const match = useMatch();
    const navigate = useNavigate();
    const client = useApolloClient();

    const bookingId = +match.params.id;
    const pathname = window.location.hash.replace('#', '');

    const isLegacyRoute = /^\/checkin\/\d+/.test(pathname);

    const [doneGuests, setDoneGuests] = useState<Set<number>>(new Set());

    const { data, loading, error } = useQuery<{
        booking: {
            id: number;
            guestName: string;
            checkin: string;
            checkout: string;
            guests: Guest[];
        };
    }>(checkinQuery, {
        variables: { id: bookingId },
        skip: !match.params.id,
    });

    const [markComplete, { loading: markCompleteLoading }] = useMutation(markCheckinCompleteMutation);

    if (!propertyId && isLegacyRoute) {
        return <PropertySelectionDialog open={true} targetPath={`/checkin/${bookingId}`} />;
    }

    if (loading) {
        return (
            <PageContainer>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {new Array(3).fill(0).map((_, index) => (
                        <Skeleton key={index} variant="rectangular" height={400} />
                    ))}
                </Box>
            </PageContainer>
        );
    }

    if (error) {
        return (
            <PageContainer>
                <Typography color="error">{error.message}</Typography>
            </PageContainer>
        );
    }

    if (!data) {
        return null;
    }

    const { booking } = data;
    const allGuestsDone = booking.guests.every(guest => doneGuests.has(guest.id));

    const handleMarkComplete = () => {
        markComplete({
            variables: {
                id: bookingId,
                fields: { hasInstructions: true },
            },
        })
            .then(async () => {
                await client.clearStore();
                navigate({
                    to: propertyId ? `/${propertyId}/home` : '/',
                });
            })
            .catch(err => {
                throw err;
            });
    };

    return (
        <PageContainer>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Box
                    sx={{
                        borderBottom: '3px solid #6366f1',
                        pb: 2,
                    }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 900,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                        }}
                    >
                        Check-in: {booking.guestName}
                    </Typography>
                    <Typography sx={{ color: '#9ca3af', mt: 1 }}>
                        {booking.checkin} - {booking.checkout}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {booking.guests.map((guest, index) => (
                        <GuestCheckinCard
                            key={guest.id}
                            guest={guest}
                            bookingId={bookingId}
                            guestIndex={index}
                            isDone={doneGuests.has(guest.id)}
                            onMarkDone={() => {
                                setDoneGuests(prev => new Set(Array.from(prev).concat(guest.id)));
                            }}
                        />
                    ))}
                </Box>

                <Box
                    sx={{
                        borderTop: '3px solid #374151',
                        pt: 3,
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}
                >
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={!allGuestsDone || markCompleteLoading}
                        onClick={handleMarkComplete}
                        sx={{
                            borderRadius: 0,
                            border: '2px solid',
                            borderColor: 'secondary.main',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 4,
                            py: 1.5,
                            '&:disabled': {
                                borderColor: '#374151',
                            },
                        }}
                    >
                        {markCompleteLoading ? 'Completing...' : 'Complete Check-in'}
                    </Button>
                </Box>
            </Box>
        </PageContainer>
    );
});

export default CheckinPage;
